import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search, FileText, Briefcase, Check } from 'lucide-react';
import { useSubscriptionWizard } from './SubscriptionWizardContext';
import SubscriptionWizardLayout from './SubscriptionWizardLayout';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { HubspotOpportunityWithCustomer } from '@/types/hubspot';
import { getHubspotOpportunities } from '@/services/business-opportunities';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

interface OpportunityWithSubscriptionStatus extends HubspotOpportunityWithCustomer {
  hasExistingSubscription: boolean;
}

const Step1bOpportunity = () => {
  const navigate = useNavigate();
  const { state, setSelectedOpportunity } = useSubscriptionWizard();
  const [opportunities, setOpportunities] = useState<OpportunityWithSubscriptionStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!state.selectedCustomer) {
      // Redirect back to step 1 if no customer is selected
      navigate('../step1', { relative: 'path' });
      return;
    }

    fetchOpportunities();
  }, [state.selectedCustomer, navigate, searchQuery]); // Adiciona searchQuery para rebuscar ao digitar

  const fetchOpportunities = async () => {
    if (!state.selectedCustomer) return;

    try {
      setLoading(true);
      console.log(`[fetchOpportunities] Buscando oportunidades para o cliente ID: ${state.selectedCustomer.id}`);

      const customerOpportunities = await getHubspotOpportunities(state.selectedCustomer.id);
      console.log(`[fetchOpportunities] Encontradas ${customerOpportunities.length} oportunidades brutas do HubSpot.`);

      let filteredByStage = customerOpportunities.filter(opp => opp.stage_label === 'Negócio fechado');
      console.log(`[fetchOpportunities] Encontradas ${filteredByStage.length} oportunidades após filtro de 'Negócio fechado'.`);

      if (searchQuery) {
        filteredByStage = filteredByStage.filter(
          opp =>
            opp.dealname.toLowerCase().includes(searchQuery.toLowerCase()) ||
            opp.stage_label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            opp.amount?.toString().includes(searchQuery)
        );
      }

      if (filteredByStage.length === 0) {
        setOpportunities([]);
        setLoading(false);
        return;
      }

      const opportunityIds = filteredByStage.map(opp => opp.id);
      const { data: existingSubscriptions, error: subsError } = await supabase
        .from('subscriptions')
        .select('hubspot_opportunity_id')
        .in('hubspot_opportunity_id', opportunityIds);

      if (subsError) {
        console.error('Erro ao buscar assinaturas existentes:', subsError);
        toast.error('Não foi possível verificar as assinaturas existentes.');
      }
      
      const existingSubscriptionIds = new Set(
        existingSubscriptions?.map(sub => sub.hubspot_opportunity_id) || []
      );

      const opportunitiesWithSubscriptionStatus = filteredByStage.map(opp => ({
        ...opp,
        hasExistingSubscription: existingSubscriptionIds.has(opp.id),
      }));
      
      setOpportunities(opportunitiesWithSubscriptionStatus);
      
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      toast.error('Falha ao carregar oportunidades do cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleOpportunityClick = (opportunity: OpportunityWithSubscriptionStatus) => {
    if (opportunity.hasExistingSubscription) return;
    
    // Se a oportunidade já está selecionada, deseleciona
    if (state.selectedOpportunity?.id === opportunity.id) {
      setSelectedOpportunity(null);
    } else {
      // Seleciona a nova oportunidade - remove a propriedade hasExistingSubscription
      const { hasExistingSubscription, ...opportunityWithoutStatus } = opportunity;
      setSelectedOpportunity(opportunityWithoutStatus);
    }
  };

  const handleSkip = () => {
    setSelectedOpportunity(null);
    navigate('../step1c', { relative: 'path' });
  };

  // Função para formatar o estágio da oportunidade
  const getStageLabel = (opportunity: OpportunityWithSubscriptionStatus | HubspotOpportunityWithCustomer) => {
    // Usar apenas o stage_label da view
    return opportunity.stage_label || 'Desconhecido';
  };
  
  // Função para obter classe CSS baseada no estágio
  const getStageClass = (stage?: string) => {
    // Sempre usar cor verde para os labels
    return 'bg-green-100 text-green-800 border-green-300';
  };

  return (
    <SubscriptionWizardLayout
      currentStep={1}
      totalSteps={6}
      title="Nova Assinatura - Selecionar Oportunidade"
      description={`Selecione uma oportunidade do HubSpot para ${state.selectedCustomer?.name || 'o cliente'}`}
      onNext={() => navigate('../step1c', { relative: 'path' })}
      onPrevious={() => navigate('../step1', { relative: 'path' })}
      isNextDisabled={!state.selectedOpportunity}
    >
      <div className="space-y-6">
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar oportunidades..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="border rounded-md">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : opportunities.length === 0 ? (
            <div className="text-center p-8 space-y-4">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground" />
              <div className="text-muted-foreground">
                Nenhuma oportunidade encontrada para este cliente no HubSpot
              </div>
            </div>
          ) : (
            <div className="divide-y">
              {opportunities.map((opportunity) => (
                <div
                  key={opportunity.id}
                  className={`p-4 ${
                    opportunity.hasExistingSubscription 
                      ? 'bg-gray-50 cursor-not-allowed opacity-60' 
                      : state.selectedOpportunity?.id === opportunity.id
                        ? 'bg-primary/10 border-2 border-primary cursor-pointer'
                        : 'hover:bg-muted/50 cursor-pointer border-2 border-transparent'
                  }`}
                  onClick={() => handleOpportunityClick(opportunity)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Briefcase className="h-4 w-4 mr-2 text-muted-foreground" />
                      <div className="font-extrabold">{opportunity.dealname}</div>
                      {state.selectedOpportunity?.id === opportunity.id && (
                        <Check className="h-4 w-4 ml-2 text-primary" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className={getStageClass(opportunity.dealstage)}>
                        {getStageLabel(opportunity)}
                      </Badge>
                      {opportunity.hasExistingSubscription && (
                        <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-300">
                          Assinatura Existente
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    Valor: {formatCurrency(opportunity.amount || 0, opportunity.deal_currency_code || 'BRL')}
                  </div>
                  {opportunity.closedate && (
                    <div className="text-sm text-muted-foreground">
                      Data de fechamento: {formatDate(opportunity.closedate)}
                    </div>
                  )}
                  {opportunity.hasExistingSubscription && (
                    <div className="mt-2 text-sm text-orange-600 font-medium">
                      ⚠️ Esta oportunidade já possui uma assinatura criada e não pode ser selecionada
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {state.selectedOpportunity && (
          <div className="bg-muted p-4 rounded-md">
            <div className="text-sm font-extrabold">Oportunidade Selecionada</div>
            <div className="font-extrabold mt-1 flex items-center justify-between">
              <div className="flex items-center">
                <Briefcase className="h-4 w-4 mr-2 text-muted-foreground" />
                {state.selectedOpportunity.dealname}
              </div>
              <Badge variant="outline" className={getStageClass(state.selectedOpportunity.dealstage)}>
                {getStageLabel(state.selectedOpportunity)}
              </Badge>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Valor: {formatCurrency(state.selectedOpportunity.amount || 0, state.selectedOpportunity.deal_currency_code || 'BRL')}
            </div>
          </div>
        )}
      </div>
    </SubscriptionWizardLayout>
  );
};

export default Step1bOpportunity; 